import limit from "p-limit";

import type { MissionItemInt } from "../gen/schema";
import { range } from "./common";
import type { Mavlink } from "./mavlink";

export const createVehicleMissions = (mavlink: Mavlink) => {
  const read = async (cancel?: Promise<never>) => {
    for (;;)
      try {
        const { count } = await mavlink.retry(
          "MISSION_REQUEST_LIST",
          {
            targetSystem: 0,
            targetComponent: 0,
            missionType: "MISSION",
          },
          mavlink.receiveOne("MISSION_COUNT"),
        );

        console.log(`Mission waypoints: ${count}`);

        const ack = mavlink
          .receiveOne("MISSION_ACK")
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
          .then(() => Promise.reject("canceled"));

        const throttle = limit(8);

        const items = await Promise.all(
          range(0, count).map(seq =>
            throttle(async () => {
              const item = await mavlink.retry(
                "MISSION_REQUEST_INT",
                {
                  targetSystem: 0,
                  targetComponent: 0,
                  seq,
                  missionType: "MISSION",
                },
                mavlink.receiveOne("MISSION_ITEM_INT", _ => _.seq === seq),
                cancel,
              );
              console.log(`Loaded mission item ${seq}`);
              return item;
            }),
          ),
        );

        mavlink.send("MISSION_ACK", {
          targetSystem: 0,
          targetComponent: 0,
          type: "MAV_MISSION_ACCEPTED",
          missionType: "MISSION",
        });

        await Promise.race([ack.catch(() => []), Promise.resolve()]);

        console.log("Loading mission complete");

        return items;
      } catch (error) {
        if (error === "canceled") {
          console.log("Mission load canceled. Retrying...");
          continue;
        } else throw error;
      }
  };

  const write = async (items: MissionItemInt[], cancel?: Promise<never>) => {
    console.log(`Writing mission with ${items.length} waypoints`);

    await mavlink.retry(
      "MISSION_COUNT",
      {
        targetSystem: 0,
        targetComponent: 0,
        count: items.length,
        missionType: "MISSION",
      },
      mavlink.receiveOne("MISSION_REQUEST", _ => _.seq === 0),
      cancel,
    );

    const complete = mavlink.receiveOne(
      "MISSION_ACK",
      _ => _.type !== "MAV_MISSION_INVALID_SEQUENCE",
      cancel,
    );

    await Promise.all(
      items.map(async (item, seq) => {
        console.log(`Sending mission item ${seq}`);
        item.seq = seq;
        const last = seq === items.length - 1;
        const missionRequest = (seq: number) =>
          Promise.race([
            mavlink.receiveOne("MISSION_REQUEST", _ => _.seq === seq),
            complete,
          ]);
        if (last) await missionRequest(seq);

        await mavlink.retry(
          "MISSION_ITEM_INT",
          item,
          Promise.race([complete, last ? complete : missionRequest(seq + 1)]),
          cancel,
        );
        console.log(`Sent mission item ${seq}`);
      }),
    );

    const ack = await complete;

    if (ack.type !== "MAV_MISSION_ACCEPTED") {
      console.warn(`Mission write failure: ${ack.type}`);
      return;
    }

    console.log("Mission write complete");
  };

  return { read, write };
};
